import {
  GITFLIC_PIPELINES_PROXY_URL,
  GITFLIC_READ_SCOPES,
  GITFLIC_WRITE_SCOPES,
} from '../utils/constants';
import { GitflicPipelinesApi } from './GitflicPipelinesApi';
import {
  GitflicArtifact,
  GitflicArtifactListResponse,
  GitflicBranch,
  GitflicBranchListResponse,
  GitflicCicdVariable,
  GitflicCicdVariableListResponse,
  GitflicIssue,
  GitflicIssueListResponse,
  GitflicIssueStatus,
  GitflicJob,
  GitflicJobListResponse,
  GitflicMergeRequest,
  GitflicMergeRequestListResponse,
  GitflicMergeRequestStatus,
  GitflicPipeline,
  GitflicPipelineListResponse,
  GitflicPipelinesClientOptions,
  GitflicPipelineStartVariable,
  GitflicSort,
  GitflicTag,
  GitflicTagListResponse,
} from './types';

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_SORT: GitflicSort = { field: 'createdAt', direction: 'DESC' };

export class GitflicPipelinesApiClient implements GitflicPipelinesApi {
  private readonly discoveryApi;
  private readonly fetchApi;
  private readonly gitflicAuthApi;
  private readonly proxyPath: string;

  constructor(options: GitflicPipelinesClientOptions) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
    this.gitflicAuthApi = options.gitflicAuthApi;
    this.proxyPath = options.proxyPath ?? GITFLIC_PIPELINES_PROXY_URL;
  }

  async listBranches(projectSlug: string): Promise<GitflicBranch[]> {
    const response = await this.request<GitflicBranchListResponse>(
      projectSlug,
      '/branch?page=0&size=20',
    );

    return response._embedded?.branchList ?? [];
  }

  async listTags(projectSlug: string): Promise<GitflicTag[]> {
    const response = await this.request<GitflicTagListResponse>(
      projectSlug,
      '/tag?page=0&size=20',
    );

    return (
      response._embedded?.tagList ?? response._embedded?.restTagModelList ?? []
    );
  }

  async listCicdVariables(projectSlug: string): Promise<GitflicCicdVariable[]> {
    const response = await this.request<GitflicCicdVariableListResponse>(
      projectSlug,
      '/setting/cicd/variable',
    );

    return response._embedded?.restPipelineJobVariableModelList ?? [];
  }

  async listProjectPipelines(
    projectSlug: string,
    options?: {
      page?: number;
      size?: number;
      sort?: { field: string; direction: 'ASC' | 'DESC' };
      branch?: string;
      tag?: string;
    },
  ): Promise<{
    items: GitflicPipeline[];
    page?: GitflicPipelineListResponse['page'];
  }> {
    const page = options?.page ?? 0;
    const size = options?.size ?? 10;
    const query = new URLSearchParams({
      page: String(page),
      size: String(size),
    });

    if (options?.sort) {
      query.set('sort', `${options.sort.field},${options.sort.direction}`);
    }
    if (options?.branch) {
      query.set('branch', options.branch);
    }
    if (options?.tag) {
      query.set('tag', options.tag);
    }

    const response = await this.request<GitflicPipelineListResponse>(
      projectSlug,
      `/cicd/pipeline?${query.toString()}`,
    );

    return {
      items: response._embedded?.restPipelineModelList ?? [],
      page: response.page,
    };
  }

  async listIssues(
    projectSlug: string,
    options?: {
      page?: number;
      size?: number;
      sort?: GitflicSort;
      statuses?: GitflicIssueStatus[];
    },
  ): Promise<{
    items: GitflicIssue[];
    page?: GitflicIssueListResponse['page'];
  }> {
    const sort = options?.sort;
    const query = this.buildListQuery({
      ...options,
      sort: sort ? `${sort.field},${sort.direction}` : 'createdAt',
    });
    const response = await this.request<GitflicIssueListResponse>(
      projectSlug,
      `/issue?${query.toString()}`,
    );

    return {
      items:
        response._embedded?.issueList ??
        response._embedded?.issueModelList ??
        response._embedded?.restIssueModelList ??
        [],
      page: response.page,
    };
  }

  async listMergeRequests(
    projectSlug: string,
    options?: {
      page?: number;
      size?: number;
      sort?: GitflicSort;
      statuses?: GitflicMergeRequestStatus[];
    },
  ): Promise<{
    items: GitflicMergeRequest[];
    page?: GitflicMergeRequestListResponse['page'];
  }> {
    const response = await this.request<GitflicMergeRequestListResponse>(
      projectSlug,
      `/merge-request/list?${this.buildListQuery(options).toString()}`,
    );

    return {
      items: response._embedded?.mergeRequestModelList ?? [],
      page: response.page,
    };
  }

  async listPipelineJobs(
    projectSlug: string,
    pipelineLocalId: number,
    options?: {
      page?: number;
      size?: number;
      sort?: { field: string; direction: 'ASC' | 'DESC' };
    },
  ): Promise<{ items: GitflicJob[]; page?: GitflicJobListResponse['page'] }> {
    const page = options?.page ?? 0;
    const size = options?.size ?? 100;
    const sort = options?.sort
      ? `&sort=${encodeURIComponent(
          `${options.sort.field},${options.sort.direction}`,
        )}`
      : '';
    const response = await this.request<GitflicJobListResponse>(
      projectSlug,
      `/cicd/pipeline/${pipelineLocalId}/jobs?page=${page}&size=${size}${sort}`,
    );

    return {
      items: response._embedded?.restPipelineJobModelList ?? [],
      page: response.page,
    };
  }

  async listPipelineArtifacts(
    projectSlug: string,
    pipelineLocalId: number,
  ): Promise<GitflicArtifact[]> {
    const response = await this.request<GitflicArtifactListResponse>(
      projectSlug,
      `/cicd/pipeline/${pipelineLocalId}/artifacts`,
    );

    return response._embedded?.restPipelineJobArtifactModelList ?? [];
  }

  async startPipeline(
    projectSlug: string,
    options?: {
      refName?: string;
      isTag?: boolean;
      variables?: GitflicPipelineStartVariable[];
    },
  ): Promise<{ pipelineUuid?: string }> {
    const body: {
      refName?: string;
      isTag?: boolean;
      variables?: GitflicPipelineStartVariable[];
    } = {};

    if (options?.refName) {
      body.refName = options.refName;
      body.isTag = options.isTag ?? false;
    }

    if (options?.variables?.length) {
      body.variables = options.variables;
    }

    const response = await this.request<{ pipeline_uuid?: string }>(
      projectSlug,
      '/cicd/pipeline/start',
      {
        method: 'POST',
        body: JSON.stringify(body),
      },
      { writeAccess: true },
    );

    return {
      pipelineUuid: response.pipeline_uuid,
    };
  }

  async restartPipeline(
    projectSlug: string,
    pipelineLocalId: number,
  ): Promise<void> {
    await this.request(
      projectSlug,
      `/cicd/pipeline/${pipelineLocalId}/restart`,
      { method: 'POST' },
      { writeAccess: true, expectJson: false },
    );
  }

  async cancelPipeline(
    projectSlug: string,
    pipelineLocalId: number,
  ): Promise<void> {
    await this.request(
      projectSlug,
      `/cicd/pipeline/${pipelineLocalId}/cancel`,
      { method: 'POST' },
      { writeAccess: true, expectJson: false },
    );
  }

  async deletePipeline(
    projectSlug: string,
    pipelineLocalId: number,
  ): Promise<void> {
    await this.request(
      projectSlug,
      `/cicd/pipeline/${pipelineLocalId}/delete`,
      { method: 'DELETE' },
      { writeAccess: true, expectJson: false },
    );
  }

  async restartJob(projectSlug: string, jobLocalId: number): Promise<void> {
    await this.request(
      projectSlug,
      `/cicd/job/${jobLocalId}/restart`,
      { method: 'POST' },
      { writeAccess: true, expectJson: false },
    );
  }

  async cancelJob(projectSlug: string, jobLocalId: number): Promise<void> {
    await this.request(
      projectSlug,
      `/cicd/job/${jobLocalId}/cancel`,
      { method: 'POST' },
      { writeAccess: true, expectJson: false },
    );
  }

  async downloadArtifact(downloadUrl: string): Promise<Blob> {
    const token = await this.gitflicAuthApi.getAccessToken(GITFLIC_READ_SCOPES);
    const proxyUrl = await this.proxyDownloadUrl(downloadUrl);
    const response = await this.fetchApi.fetch(proxyUrl, {
      headers: {
        Authorization: `token ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(await this.buildErrorMessage(response));
    }

    return await response.blob();
  }

  private async request<T = unknown>(
    projectSlug: string,
    path: string,
    init?: RequestInit,
    options?: { writeAccess?: boolean; expectJson?: boolean },
  ): Promise<T> {
    const token = await this.gitflicAuthApi.getAccessToken(
      options?.writeAccess
        ? [...GITFLIC_READ_SCOPES, ...GITFLIC_WRITE_SCOPES]
        : GITFLIC_READ_SCOPES,
    );

    const apiUrl = await this.apiUrl(projectSlug);
    const response = await this.fetchApi.fetch(`${apiUrl}${path}`, {
      ...init,
      headers: {
        Authorization: `token ${token}`,
        ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
        ...(init?.headers ?? {}),
      },
    });

    if (!response.ok) {
      throw new Error(await this.buildErrorMessage(response));
    }

    if (options?.expectJson === false) {
      return undefined as T;
    }

    const contentType = response.headers.get('content-type') ?? '';
    if (!contentType.includes('application/json')) {
      return undefined as T;
    }

    return (await response.json()) as T;
  }

  private async apiUrl(projectSlug: string): Promise<string> {
    const { ownerAlias, projectAlias } = this.parseProjectSlug(projectSlug);
    const proxyBaseUrl = await this.discoveryApi.getBaseUrl('proxy');

    return `${proxyBaseUrl}${this.proxyPath}/project/${encodeURIComponent(
      ownerAlias,
    )}/${encodeURIComponent(projectAlias)}`;
  }

  private buildListQuery(options?: {
    page?: number;
    size?: number;
    sort?: GitflicSort | string;
    statuses?: string[];
  }): URLSearchParams {
    const sort = options?.sort ?? DEFAULT_SORT;
    const query = new URLSearchParams({
      page: String(options?.page ?? 0),
      size: String(options?.size ?? DEFAULT_PAGE_SIZE),
      sort: typeof sort === 'string' ? sort : `${sort.field},${sort.direction}`,
    });

    if (options?.statuses?.length) {
      query.set('status', options.statuses.join(','));
    }

    return query;
  }

  private async proxyDownloadUrl(downloadUrl: string): Promise<string> {
    const proxyBaseUrl = await this.discoveryApi.getBaseUrl('proxy');

    try {
      const url = new URL(downloadUrl);
      const restPath = url.pathname.startsWith('/rest-api')
        ? url.pathname.slice('/rest-api'.length)
        : url.pathname;

      return `${proxyBaseUrl}${this.proxyPath}${restPath}${url.search}`;
    } catch {
      const restPath = downloadUrl.startsWith('/rest-api')
        ? downloadUrl.slice('/rest-api'.length)
        : downloadUrl;

      return `${proxyBaseUrl}${this.proxyPath}${restPath}`;
    }
  }

  private parseProjectSlug(projectSlug: string): {
    ownerAlias: string;
    projectAlias: string;
  } {
    const [ownerAlias, projectAlias, ...rest] = projectSlug.split('/');

    if (!ownerAlias || !projectAlias || rest.length > 0) {
      throw new Error(
        `Expected Gitflic project slug in format owner/project, got "${projectSlug}"`,
      );
    }

    return { ownerAlias, projectAlias };
  }

  private async buildErrorMessage(response: Response): Promise<string> {
    const fallback = `Gitflic request failed with ${response.status} ${response.statusText}`;
    const contentType = response.headers.get('content-type') ?? '';

    if (contentType.includes('application/json')) {
      try {
        const body = await response.json();
        const message =
          body?.error?.message ?? body?.message ?? body?.error ?? body?.detail;

        if (message) {
          return `${fallback}: ${message}`;
        }
      } catch {
        return fallback;
      }
    }

    try {
      const text = await response.text();
      return text ? `${fallback}: ${text}` : fallback;
    } catch {
      return fallback;
    }
  }
}
